import { ArrowUpRight, KeyRound } from "lucide-react";
import type { CSSProperties } from "react";
import { Link } from "../router";

import type { LicenseSummary } from "../types";
import { Capacity, EmptyState, formatDate, StatusBadge } from "./ui";

export function ProductMark({ product }: { product: LicenseSummary["product"] }) {
  if (product.logoUrl) {
    return <img alt="" className="product-mark" src={product.logoUrl} />;
  }
  return (
    <span
      aria-hidden="true"
      className="product-mark product-mark--fallback"
      style={{ "--product-accent": product.accentColor || "#28c7f7" } as CSSProperties}
    >
      <i /><i /><i /><i /><i />
    </span>
  );
}

export function LicenseRegistry({
  compact = false,
  licenses,
}: {
  compact?: boolean;
  licenses: LicenseSummary[];
}) {
  if (licenses.length === 0) {
    return (
      <EmptyState
        action={<Link className="button button--primary" to="/add-license">Add a license</Link>}
        description="Sign in with the buyer Discord account or attach the key you received from oCloud Studio."
        icon={<KeyRound size={24} />}
        title="No licenses in this workspace"
      />
    );
  }
  return (
    <div className={`registry ${compact ? "registry--compact" : ""}`}>
      <div className="registry__head" aria-hidden="true">
        <span>Product</span>
        <span>License</span>
        <span>Status</span>
        <span>IP slots</span>
        <span>Activations</span>
        <span>Expiry</span>
        <span />
      </div>
      {licenses.map((license) => (
        <article className="registry-row" key={license.id}>
          <div className="registry-row__product">
            <ProductMark product={license.product} />
            <span>
              <strong>{license.product.name}</strong>
              <small>{license.product.slug}</small>
            </span>
          </div>
          <div className="registry-row__key">
            <code>{license.keyPrefix}••••••••</code>
            <small>{license.accessLevel.toLowerCase()} access</small>
          </div>
          <div data-label="Status"><StatusBadge status={license.status} /></div>
          <div data-label="IP slots">{license.isPublic ? <span className="registry-unlimited">{license.usedIpAddresses} observed · Unlimited</span> : <Capacity total={license.maxIpAddresses} used={license.usedIpAddresses} />}</div>
          <div data-label="Activations">{license.isPublic ? <span className="registry-unlimited">{license.usedActivations} observed · Unlimited</span> : <Capacity total={license.maxActivations} used={license.usedActivations} />}</div>
          <div className="registry-row__date" data-label="Expiry">
            <strong>{formatDate(license.expiresAt)}</strong>
            <small>{license.expiresAt ? expiryCaption(license.expiresAt) : "No expiry"}</small>
          </div>
          <Link aria-label={`View ${license.product.name}`} className="row-link" to={`/licenses/${license.id}`}>
            View <ArrowUpRight size={15} />
          </Link>
        </article>
      ))}
    </div>
  );
}

function expiryCaption(value: string) {
  const days = Math.ceil((new Date(value).getTime() - Date.now()) / 86_400_000);
  if (days < 0) return "Expired";
  if (days === 0) return "Expires today";
  return `${days} day${days === 1 ? "" : "s"} remaining`;
}
